import { LitElement, css, html } from "lit"
import { unsafeHTML } from "lit/directives/unsafe-html.js"

const { apiFetch } = window.wp

export class Search extends LitElement {
	debounceTimeout = 0

	static properties = {
		query: { type: String },
		open: { type: Boolean, reflect: true },
		results: { type: Array },
		loading: { type: Boolean },
	}

	static styles = css`
		* {
			box-sizing: border-box;
		}

		:host {
			position: relative;
			opacity: 1;
		}
		button {
			background: none;
			border: none;
			cursor: pointer;
			outline: none;
		}
		.search-modal {
			position: fixed;
			top: 0;
			left: 0;
			width: 100%;
			height: 100%;
			background-color: rgba(0, 0, 0, 0.8);
			display: flex;
			justify-content: center;
			z-index: 1000;
			padding: 10% 5px;
		}

		input {
			width: 100%;
			padding: 9px;
			border-radius: 4px;
			border: 1px solid #999;
			font-size: 20px;
			line-height: 1.2;
		}

		.search-content {
			background: white;
			padding: 60px 30px;
			border-radius: 8px;
			width: 90%;
			max-width: 600px;
			position: relative;
			height: max-content;
		}

		.close-button {
			position: absolute;
			top: 10px;
			right: 10px;
			background: none;
			border: none;
			font-size: 24px;
			cursor: pointer;
		}

		.search-title {
			display: block;
			font-size: 24px;
			font-weight: bold;
		}

		.search-field {
			display: flex;
			gap: 10px;
			margin-bottom: 20px;
			align-items: flex-end;
		}

		.search-field button {
			background-color: #35572c;
			transition: background-color 0.2s ease;
			height: max-content;
			border-radius: 4px;
			padding: 8px;
		}
		.search-field button:hover {
			background-color: #4c7d3f;
		}

		.search-results {
			margin-top: 20px;
			transition: height 0.3s ease;
			height: 50px;
		}
		.search-results.has-content {
			height: max(400px, 60vh);
			max-height: 100%;
			overflow-y: auto;
		}

		.search-result-item {
			margin-bottom: 10px;
			border: 1px solid #aaa;
			padding: 10px;
			border-radius: 4px;
			transition: background-color 0.2s ease;
		}

		.search-result-item:hover {
			background-color: #f5f5f5;
		}

		.search-result-item a {
			font-size: 18px;
			color: #1c1e1d;
			text-decoration: none;
			display: flex;
			align-items: center;
			gap: 10px;
		}

		.search-result-item img {
			max-width: 50px;
			max-height: 50px;
			margin-right: 10px;
			vertical-align: middle;
			border-radius: 4px;
		}
	`

	constructor() {
		super()
		this.query = ""
		this.open = false
		this.results = []
		this.loading = false
	}

	doSearch() {
		if (this.debounceTimeout) {
			clearTimeout(this.debounceTimeout)
		}
		this.debounceTimeout = setTimeout(async () => {
			this.loading = true
			this.results = []
			const results = await apiFetch({
				path: `/wc/store/products/?search=${this.query}&per_page=15`,
			})
			this.results = results
			this.loading = false
		}, 300)
	}

	async loadMoreResults() {
		const results = await apiFetch({
			path: `/wc/store/products/?search=${this.query}&per_page=15&page=${
				Math.ceil(this.results.length / 15) + 1
			}`,
		})
		this.results = [...this.results, ...results]
	}

	renderResults(results) {
		results.forEach(product => {
			const productLink = document.createElement("a")
			productLink.href = product.permalink
			productLink.textContent = product.name
		})
	}

	renderButton() {
		return html`<button
			@click="${() => {
				this.open = true
			}}"
			class="cf-search-form">
			<svg
				xmlns="http://www.w3.org/2000/svg"
				width="24"
				height="24"
				viewBox="0 0 24 24"
				fill="none">
				<path
					d="M11.5 21C16.7467 21 21 16.7467 21 11.5C21 6.25329 16.7467 2 11.5 2C6.25329 2 2 6.25329 2 11.5C2 16.7467 6.25329 21 11.5 21Z"
					stroke="#fff"
					stroke-width="1.5"
					stroke-linecap="round"
					stroke-linejoin="round" />
				<path
					d="M22 22L20 20"
					stroke="#fff"
					stroke-width="1.5"
					stroke-linecap="round"
					stroke-linejoin="round" />
			</svg>
		</button>`
	}

	render() {
		return html` ${this.renderButton()}
		${this.open
			? html`<div class="search-modal">
					<div class="search-content">
						<button
							class="close-button"
							@click="${() => {
								this.open = false
							}}">
							<svg
								xmlns="http://www.w3.org/2000/svg"
								width="18"
								height="18"
								viewBox="0 0 13 13"
								fill="none">
								<path
									d="M8.4846 6.36393L12.7272 10.6066L10.6059 12.7279L6.36328 8.48525L2.12064 12.7279L-0.000679672 10.6066L4.24196 6.36393L-0.000679672 2.12129L2.12064 -3.51667e-05L6.36328 4.24261L10.6059 -3.51667e-05L12.7272 2.12129L8.4846 6.36393Z"
									fill="#000" />
							</svg>
						</button>
						<div class="search-field">
							<label for="search-input" class="search-title"
								>Sök efter produkter:
								<input
									id="search-input"
									name="search-input"
									type="search"
									placeholder="Ange sökord"
									@input="${e => {
										this.query = e.target.value
										this.doSearch()
									}}" />
							</label>
						</div>
						<div
							class="search-results ${this.results.length > 0
								? "has-content"
								: ""}">
							${!this.loading
								? null
								: html`<svg
										width="24"
										height="24"
										viewBox="0 0 24 24"
										xmlns="http://www.w3.org/2000/svg">
										<style>
											.spinner_qM83 {
												animation: spinner_8HQG 1.05s infinite;
											}
											.spinner_oXPr {
												animation-delay: 0.1s;
											}
											.spinner_ZTLf {
												animation-delay: 0.2s;
											}
											@keyframes spinner_8HQG {
												0%,
												57.14% {
													animation-timing-function: cubic-bezier(
														0.33,
														0.66,
														0.66,
														1
													);
													transform: translate(0);
												}
												28.57% {
													animation-timing-function: cubic-bezier(
														0.33,
														0,
														0.66,
														0.33
													);
													transform: translateY(-6px);
												}
												100% {
													transform: translate(0);
												}
											}
										</style>
										<circle class="spinner_qM83" cx="4" cy="12" r="3" />
										<circle
											class="spinner_qM83 spinner_oXPr"
											cx="12"
											cy="12"
											r="3" />
										<circle
											class="spinner_qM83 spinner_ZTLf"
											cx="20"
											cy="12"
											r="3" />
									</svg>`}
							${this.results.map(
								product =>
									html`<div class="search-result-item">
										<a href="${product.permalink}">
											${product.images[0]?.thumbnail
												? html`<img
														src="${product.images[0]?.thumbnail}"
														alt="${product.name}" />`
												: null}
											${unsafeHTML(product.name)}
										</a>
									</div>`,
							)}
							${this.results.length >= 15 && !this.loading
								? html`<div style="text-align:center; margin-top:20px;">
										<button
											@click="${() => this.loadMoreResults()}"
											style="background-color:#35572c; color:#fff; border:none; padding:10px 20px; border-radius:4px; cursor:pointer; font-size:16px;">
											Ladda fler resultat
										</button>
									</div>`
								: null}
						</div>
					</div>
				</div>`
			: null}`
	}
}
